/* eslint-disable canvas/no-spread-arguments */
import {
  type BaseState,
  type BaseEvent,
  type BaseEventLookup,
  type BaseContext,
  type StateConfig,
  type StateMachineConfig,
  type Transition,
  type StateTransitionConfig,
  type StatePassThroughConfig,
  type StateCommand,
  type StateGuard,
  type Effect,
  type Effects,
  type AsyncStateConfig,
  type AsyncRejectedEvent,
  type AsyncRejectedEventKey,
  type AsyncEventLookup,
  type AsyncResolvedEventKey,
  type AsyncResolvedEvent,
} from '../state-machine-types.ts';
import { createMachine } from '../state-machine.ts';

import {
  type Builder,
  type StateBuilder,
  type TransientStateBuilder,
  type EffectNameToStateEffect,
  type TransitionBuilder,
  type EffectName,
  type EffectLookup,
  type TransitionOpts,
  type AsyncStateBuilder,
} from './state-machine-builder-types.ts';

export const effectNameMap: EffectNameToStateEffect = {
  enter: 'onEnter',
  're-enter': 'onReEnter',
  exit: 'onExit',
  execute: 'onExecute',
};

type StateTree<
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
> = { states?: Partial<Record<S, StateConfig<S, E, L, C>>> };

/**
 * Use this builder to create state machine configuration with ease.
 * @returns A Builder API for incrementally building
 */
export const builder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
>(): Builder<S, E, L, C> => {
  type StateBuilderAPI = StateBuilder<S, E, L, C>;
  type TransientStateBuilderAPI = TransientStateBuilder<S, E, L, C>;

  const config = {} as StateMachineConfig<S, E, L, C>;
  config.states = {};

  const initial = (state: S) => {
    config.initial = state;
    return { context };
  };

  const context = (context: C) => {
    config.context = context;
    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const state = (name: S, doBuild: (builder: StateBuilderAPI) => StateBuilderAPI) => {
    const builder = stateBuilder(name, config);
    doBuild(builder).build();
    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const transientState = (
    name: S,
    doBuild: (builder: TransientStateBuilderAPI) => TransientStateBuilderAPI,
  ) => {
    const builder = transientStateBuilder(name, config);
    doBuild(builder).build();
    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const when = <T extends E['type']>(
    on: T,
    doBuild: (
      builder: TransitionBuilder<T, S, E, L, C>,
    ) => Transition<S, C, L, T> | Transition<S, C, L, T>[],
  ) => {
    const builder = transitionBuilder<S, E, L, C, T>();
    const newTransitions = doBuild(builder);

    if (!config.globalTransitions) {
      config.globalTransitions = {};
    }

    const transitionsForEvent = (config.globalTransitions[on] ??= []);

    if (Array.isArray(newTransitions)) {
      transitionsForEvent.push(...newTransitions);
    } else {
      transitionsForEvent.push(newTransitions);
    }

    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const asyncState = (
    name: S,
    doBuild: (
      builder: Pick<AsyncStateBuilder<S, E, L, C, unknown>, 'onEnter'>,
    ) => Pick<AsyncStateBuilder<S, E, L, C, unknown>, 'build'>,
  ) => {
    const builder = asyncStateBuilder(name, config);
    doBuild(builder).build();
    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const use = (name: S, definition: StateTransitionConfig<S, E, L, C>) => {
    config.states[name] = definition;
    return { state, transientState, build, when, use, with: state, asyncState };
  };

  const build = () => {
    return createMachine(config);
  };

  return { initial };
};

export const stateBuilder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
>(
  name: S,
  config?: StateTree<S, E, L, C>,
  baseConfig?: StateConfig<S, E, L, C>,
): StateBuilder<S, E, L, C> => {
  type StateTransition = StateTransitionConfig<S, E, L, C>;
  type StateBuilderAPI = StateBuilder<S, E, L, C>;
  type TransientStateBuilderAPI = TransientStateBuilder<S, E, L, C>;

  // State tree defaults.
  if (config && !config.states) {
    config.states = {};
  }

  const stateConfig = (baseConfig ?? {}) as StateTransition;

  // State defaults.
  if (!stateConfig.effects) {
    stateConfig.effects = {};
  }
  if (!stateConfig.transitions) {
    stateConfig.transitions = {};
  }

  if (!stateConfig.guards) {
    stateConfig.guards = [];
  }

  const when = <T extends E['type']>(
    on: T,
    build: (
      builder: TransitionBuilder<T, S, E, L, C>,
    ) => Transition<S, C, L, T> | Transition<S, C, L, T>[],
    opts?: TransitionOpts,
  ) => {
    if (!stateConfig.transitions[on]) {
      stateConfig.transitions[on] = [];
    }
    const builder = transitionBuilder<S, E, L, C, T>();

    const transition = build(builder);
    const transitions = Array.isArray(transition) ? transition : [transition];

    const current = stateConfig.transitions[on] ?? [];

    if (!opts) {
      current.push(...transitions);
    } else {
      if ('before' in opts) {
        const idx = current.findIndex((t) => t.label === opts.before);
        if (idx === -1) {
          current.push(...transitions);
        } else {
          current.splice(idx, 0, ...transitions);
        }
      } else {
        const idx = current.findIndex((t) => t.label === opts.after);
        if (idx === -1) {
          current.push(...transitions);
        } else {
          current.splice(idx + 1, 0, ...transitions);
        }
      }
    }

    return stateApi;
  };

  const trap = () => {
    stateConfig.trap = true;
    return stateApi;
  };

  const on = <T extends EffectName>(name: T, callback: EffectLookup<T, C, E>) => {
    effectBuilder(effectNameMap[name], stateConfig, callback);
    return stateApi;
  };

  const childState = (name: S, build: (builder: StateBuilderAPI) => StateBuilderAPI) => {
    const builder = stateBuilder(name, stateConfig);
    build(builder).build();
    return stateApi;
  };

  const transientChildState = (
    name: S,
    doBuild: (builder: TransientStateBuilderAPI) => TransientStateBuilderAPI,
  ) => {
    const builder = transientStateBuilder(name, stateConfig);
    doBuild(builder).build();
    return stateApi;
  };

  const asyncState = (
    name: S,
    doBuild: (
      builder: Pick<AsyncStateBuilder<S, E, L, C, unknown>, 'onEnter'>,
    ) => Pick<AsyncStateBuilder<S, E, L, C, unknown>, 'build'>,
  ) => {
    const builder = asyncStateBuilder(name, stateConfig);
    doBuild(builder).build();
    return stateApi;
  };

  const build = () => {
    // If we're extending a base state - return the modified state; the base
    // state is already in the machine, doesn't need to be added again.
    if (baseConfig) {
      return stateConfig;
    }

    // Otherwise, add the new state to the machine.
    if (config?.states) {
      config.states[name] = stateConfig;
    }

    return stateConfig;
  };
  const guard = (guard: StateGuard) => {
    stateConfig.guards?.push(guard);
    return stateApi;
  };

  const stateApi: StateBuilderAPI = {
    when,
    on,
    build,
    state: childState,
    transientState: transientChildState,
    trap,
    guard,
    asyncState,
  };

  return stateApi;
};

export const asyncStateBuilder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
>(
  name: S,
  config?: StateTree<S, E, L, C>,
  baseConfig?: AsyncStateConfig<S, E, L, C>,
): Pick<AsyncStateBuilder<S, E, L, C, unknown>, 'onEnter'> => {
  const stateConfig = {
    ...baseConfig,
    effects: {},
    resolvedTransitions: [],
    rejectedTransitions: [],
  } as AsyncStateConfig<S, E, L, C>;

  // State tree defaults.
  if (config && !config.states) {
    config.states = {};
  }

  const onEnter = <T>(action: (ctx: C, signal: AbortSignal) => Promise<T>) => {
    stateConfig.asyncAction = action;

    return {
      whenResolved,
      whenRejected,
    } as Pick<AsyncStateBuilder<S, E, L, C, T>, 'whenResolved' | 'whenRejected'>;
  };

  const whenResolved = (
    doBuild: (
      builder: TransitionBuilder<
        AsyncResolvedEventKey,
        S,
        AsyncResolvedEvent,
        AsyncEventLookup,
        C
      >,
    ) => Transition<S, C, AsyncEventLookup, AsyncResolvedEventKey>,
  ) => {
    const builder = transitionBuilder<
      S,
      AsyncResolvedEvent,
      AsyncEventLookup,
      C,
      AsyncResolvedEventKey
    >();
    stateConfig.resolvedTransitions.push(doBuild(builder));

    return {
      whenResolved,
      whenRejected,
    };
  };

  const whenRejected = (
    doBuild: (
      builder: TransitionBuilder<
        AsyncRejectedEventKey,
        S,
        AsyncRejectedEvent,
        AsyncEventLookup,
        C
      >,
    ) => Transition<S, C, AsyncEventLookup, AsyncRejectedEventKey>,
  ) => {
    const builder = transitionBuilder<
      S,
      AsyncRejectedEvent,
      AsyncEventLookup,
      C,
      AsyncRejectedEventKey
    >();
    stateConfig.rejectedTransitions.push(doBuild(builder));

    return {
      whenRejected,
      build,
      timeoutMs,
    };
  };

  const timeoutMs = (timeout: number | ((ctx: C) => number)) => {
    stateConfig.timeout = timeout;

    return {
      build,
    };
  };

  const build = () => {
    if (config?.states) {
      config.states[name] = stateConfig;
    }
  };

  return {
    onEnter,
  };
};

export const transientStateBuilder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
>(
  name: S,
  config?: StateTree<S, E, L, C>,
): TransientStateBuilder<S, E, L, C> => {
  type TransientStateBuilderAPI = TransientStateBuilder<S, E, L, C>;

  type StateTransition = StatePassThroughConfig<S, E, L, C>;
  const stateConfig = {} as StateTransition;

  // State tree defaults.
  if (config && !config.states) {
    config.states = {};
  }

  // State defaults.
  if (!stateConfig.effects) {
    stateConfig.effects = {};
  }
  if (!stateConfig.passthroughs) {
    stateConfig.passthroughs = [];
  }

  const when = (
    build: (
      builder: TransitionBuilder<E['type'], S, E, L, C>,
    ) => Transition<S, C, L, E['type']>,
  ) => {
    const builder = transitionBuilder<S, E, L, C, E['type']>();
    stateConfig.passthroughs.push(build(builder));
    return statePassthroughApi;
  };

  const on = <T extends EffectName>(name: T, callback: EffectLookup<T, C, E>) => {
    effectBuilder(effectNameMap[name], stateConfig, callback);
    return statePassthroughApi;
  };

  const build = () => {
    if (config?.states) {
      config.states[name] = stateConfig;
    }
    return stateConfig;
  };

  const statePassthroughApi: TransientStateBuilderAPI = {
    when,
    on,
    build,
  };
  return statePassthroughApi;
};

const transitionBuilder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
  T extends E['type'],
>(): TransitionBuilder<T, S, E, L, C> => {
  const transitionConfig = {} as Transition<S, C, L, T>;

  let transitionLabel = 'unknown';
  const label = (label: string) => {
    transitionLabel = label;
    return { if: guard };
  };

  const guard = (...guards: Transition<S, C, L, T>['guard']) => {
    transitionConfig.guard = guards;
    return { context };
  };

  const context = (...context: Transition<S, C, L, T>['context']) => {
    transitionConfig.context = context;
    return { to };
  };

  const to = (state: S | StateCommand) => {
    transitionConfig.state = state;
    return { ...transitionConfig, label: transitionLabel };
  };

  const transitionApi: TransitionBuilder<T, S, E, L, C> = {
    label: label,
    if: guard,
  };
  return transitionApi;
};

const effectBuilder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext,
  T extends Effect,
>(
  name: T,
  config: StateConfig<S, E, L, C>,
  callback: Effects<C, E>[T][number],
) => {
  if (!config.effects[name]) {
    config.effects[name] = [];
  }

  const effects = config.effects[name] as Effects<C, E>[T][number][];
  effects.push(callback);
};
